import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import unitApi from '../../api/services/unitApi';
import sectionApi from '../../api/services/sectionApi';
import statisticsApi from '../../api/services/statisticsApi';
import {
  Plus,
  Search,
  BookOpen,
  Users,
  Filter,
  Loader,
} from 'lucide-react';
import ClassCard from './components/ClassCard';
import ConfirmationModal from '../../ui/ConfirmationModal';
import SettingsHeader from '../../ui/SettingsHeader';

const Classes = () => {
  const { isAdmin } = useAuth();
  const { classes: apiClasses, users: teachers, students: apiStudents, addClass, updateClass, deleteClass, loadClasses, loadUsers, loading } = useData();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [classStats, setClassStats] = useState({});
  const [deleteModal, setDeleteModal] = useState({ open: false, unitId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Load class statistics with fallback
  useEffect(() => {
    const fetchStats = async () => {
        try {
            // First try the dedicated stats API
            const response = await statisticsApi.getClassStats();
            let statsMap = {};
            let hasValidStats = false;

            if (response.success && response.data) {
                const statsArray = Array.isArray(response.data) ? response.data : 
                                  (response.data.data ? response.data.data : []);
                                  
                if (Array.isArray(statsArray) && statsArray.length > 0) {
                    statsArray.forEach(stat => {
                        const id = stat._id || stat.classId || stat.unitId;
                        if (id) {
                            statsMap[id] = stat.studentCount || stat.count || stat.students || 0;
                            hasValidStats = true;
                        }
                    });
                }
            }

            // Fallback
            if (!hasValidStats && apiClasses.length > 0) {
                const perUnitCounts = {};
                await Promise.all(apiClasses.map(async (cls) => {
                    if (!cls._id) return;
                    try {
                        const contextCount = apiStudents?.filter(s => s.enrollment?.unitId?._id === cls._id)?.length;
                        if (contextCount > 0) {
                            perUnitCounts[cls._id] = contextCount;
                        } else {
                            const studentsRes = await unitApi.getStudentsOfUnit(cls._id);
                            if (studentsRes.success && Array.isArray(studentsRes.data)) {
                                 perUnitCounts[cls._id] = studentsRes.data.length;
                            } else {
                                 perUnitCounts[cls._id] = 0;
                            }
                        }
                    } catch (err) { perUnitCounts[cls._id] = 0; }
                }));
                statsMap = perUnitCounts;
            }
            
            setClassStats(statsMap);

        } catch (error) {
        }
    };

    if (apiClasses.length > 0) {
        fetchStats();
    }
    // Ensure users (teachers) are loaded
    if (!teachers || teachers.length === 0) {
        loadUsers();
    }
  }, [apiClasses, apiStudents, teachers, loadUsers]);

  // Reset page when search changes
  useEffect(() => {
      setCurrentPage(1);
  }, [searchTerm]);

  const teacherUsers = teachers?.filter(teacher => teacher.userRole?.toLowerCase() === 'teacher');

  const filteredClasses = apiClasses.filter(cls => {
    return cls.unitName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           cls.assignedTeachers?.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           cls.assignedTeachers?.name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Calculate Pagination
  const totalPages = Math.ceil(filteredClasses.length / itemsPerPage);
  const paginatedClasses = filteredClasses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleAddClass = async (classData) => {
    const result = await addClass(classData);
    if (result.success) {
      setShowAddModal(false);
      await Promise.all([loadClasses(), loadUsers()]);
      toast.success(`"${classData.unitName}" has been created successfully!`);
    } else {
      toast.error(`Failed to create class: ${result.message}`);
    }
  };

  const handleAssignTeacher = async (unitId, teacherId) => {
    try {
      const result = await unitApi.updateUnit(unitId, { assignedTeacherId: teacherId });
      if (result.success) {
        setShowAssignModal(false);
        setSelectedClass(null);
        await Promise.all([loadClasses(), loadUsers()]);
        toast.success('Teacher assigned successfully!');
      } else {
        toast.error(`Failed to assign teacher: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Failed to assign teacher: ${error.message || 'Unknown error'}`);
    }
  };

  const handleEditClass = async (unitId, classData) => {
    const result = await updateClass(unitId, classData);
    if (result.success) {
      setShowEditModal(false);
      setSelectedClass(null);
      await loadClasses();
      toast.success('Class updated successfully!');
    } else {
      toast.error(`Failed to update class: ${result.message}`);
    }
  };

  const handleDeleteClass = (unitId) => {
    setDeleteModal({ open: true, unitId });
  };

  const confirmDeleteClass = async () => {
    if (!deleteModal.unitId) return;
    setIsDeleting(true);
    try {
      const result = await deleteClass(deleteModal.unitId);
      if (result.success) {
        toast.success("Class deleted successfully");
        setDeleteModal({ open: false, unitId: null });
      } else {
        toast.error(`Failed to delete class: ${result.message}`);
      }
    } finally {
      setIsDeleting(false);
    }
  };


  
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
       <SettingsHeader
          title="Class Management"
          description="Manage academic units, assign teachers, and track enrollments"
          showBack={false}
          icon={BookOpen}
          buttons={[
            {
              label: "Add Class",
              icon: Plus,
              onClick: () => setShowAddModal(true),
              variant: "default",
            },
          ]}
       >
          <div className="relative w-full sm:w-72">
             <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
             <input
                type="text"
                placeholder="Search classes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-gray-50/50 border border-gray-200 rounded-lg py-1.5 pl-8 pr-3 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all hover:bg-white"
             />
          </div>
       </SettingsHeader>

         {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Classes</p>
                <div className="flex items-baseline gap-2 mt-1">
                    <h3 className="text-3xl font-black text-gray-900">{apiClasses.length}</h3>
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Active</span>
                </div>
            </div>
            <div className="h-12 w-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400">
                <BookOpen size={24} />
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
             <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Faculty Coverage</p>
                <div className="flex items-baseline gap-2 mt-1">
                    <h3 className="text-3xl font-black text-gray-900">
                         {apiClasses.filter(cls => cls.assignedTeachers).length}
                         <span className="text-lg text-gray-300 font-medium">/{apiClasses.length}</span>
                    </h3>
                </div>
             </div>
             <div className="h-12 w-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400">
                <Users size={24} />
             </div>
          </div>
        </div>

        {/* Classes Grid */}
        <div className="space-y-4">
            {loading.classes && apiClasses.length === 0 ? (
                 <div className="flex flex-col items-center justify-center py-20">
                     <Loader className="w-8 h-8 animate-spin text-gray-400 mb-4" />
                     <p className="text-sm font-medium text-gray-400">Loading classes...</p>
                 </div>
            ) : apiClasses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-dashed border-gray-200">
                <div className="h-16 w-16 rounded-full flex items-center justify-center mb-4 bg-gray-50">
                    <BookOpen className="h-8 w-8 text-gray-300" />
                </div>
                <h3 className="text-base font-bold text-gray-900">No classes yet</h3>
                <p className="text-gray-500 max-w-sm text-center mt-1 mb-6 text-sm">
                   Get started by creating your first academic unit to manage students and schedules.
                </p>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="btn-primary"
                >
                Create First Class
                </button>
            </div>
            ) : filteredClasses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 bg-white rounded-xl border border-gray-200">
                <div className="h-12 w-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                    <Search className="h-6 w-6 text-gray-300" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">No results found</h3>
                <p className="text-gray-500 mt-1 text-xs">Try adjusting your search terms.</p>
                <button
                    onClick={() => setSearchTerm('')}
                    className="mt-3 px-3 py-1.5 text-xs font-bold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                >
                    Clear Search
                </button>
            </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {paginatedClasses.map((cls) => {
                        let populatedCls = { ...cls };
                        if (typeof cls.assignedTeachers === 'string' && teachers) {
                            const teacherObj = teachers.find(t => t._id === cls.assignedTeachers);
                            if (teacherObj) populatedCls.assignedTeachers = teacherObj;
                        }

                        return (
                            <ClassCard
                            key={cls._id}
                            classData={{ ...populatedCls, studentCount: classStats[cls._id] || 0 }}
                            onEdit={() => {
                                setSelectedClass(populatedCls);
                                setShowEditModal(true);
                            }}
                            onDelete={handleDeleteClass}
                            onAssign={() => {
                                setSelectedClass(populatedCls);
                                setShowAssignModal(true);
                            }}
                            onView={() => {
                                setSelectedClass(populatedCls);
                                setShowViewModal(true);
                            }}
                            />
                        );
                        })}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <div className="text-xs font-medium text-gray-500">
                             Showing <span className="font-bold text-gray-900">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-bold text-gray-900">{Math.min(currentPage * itemsPerPage, filteredClasses.length)}</span> of <span className="font-bold text-gray-900">{filteredClasses.length}</span> classes
                        </div>
                        <div className="flex items-center gap-3">
                            <select 
                                value={itemsPerPage} 
                                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                className="p-1.5 text-xs font-bold text-gray-700 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-gray-300 cursor-pointer"
                            >
                                <option value={10}>10 / page</option>
                                <option value={20}>20 / page</option>
                                <option value={50}>50 / page</option>
                                <option value={100}>100 / page</option>
                            </select>
                            
                            <div className="flex items-center gap-1">
                                <button
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <span className="px-3 text-xs font-bold text-gray-900">
                                    {currentPage} / {totalPages || 1}
                                </span>
                                <button
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>

      {/* Add Class Modal */}
      {showAddModal && (
        <AddClassModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddClass}
          teachers={teacherUsers}
        />
      )}

      {/* Assign Teacher Modal */}
      {showAssignModal && selectedClass && (
        <AssignTeacherModal
          classData={selectedClass}
          teachers={teacherUsers}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedClass(null);
          }}
          onAssign={handleAssignTeacher}
        />
      )}

      {/* Edit Class Modal */}
      {showEditModal && selectedClass && (
        <EditClassModal
          classData={selectedClass}
          onClose={() => {
            setShowEditModal(false);
            setSelectedClass(null);
          }}
          onEdit={handleEditClass}
        />
      )}

      {/* View Class Modal */}
      {showViewModal && selectedClass && (
        <ViewClassModal
          classData={selectedClass}
          onClose={() => {
            setShowViewModal(false);
            setSelectedClass(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, unitId: null })}
        onConfirm={confirmDeleteClass}
        title="Delete Class"
        message="Are you sure you want to delete this class? This action cannot be undone and all associated section data will be lost."
        confirmText="Delete Class"
        isLoading={isDeleting}
      />
    </div>

  );
};

// Add Class Modal Component
const AddClassModal = ({ onClose, onAdd, teachers }) => {
  const [formData, setFormData] = useState({
    unitName: '',
    groupCode: '',
    assignedTeacherId: '',
  });
  
  // Dynamic Section State
  const [sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddSection = () => {
    if (currentSection.trim()) {
      if (!sections.includes(currentSection.trim())) {
        setSections([...sections, currentSection.trim()]);
        setCurrentSection("");
      } else {
        toast.error("Section already added");
      }
    }
  };

  const handleRemoveSection = (index) => {
    const newSections = [...sections];
    newSections.splice(index, 1);
    setSections(newSections);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSection();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const classData = {
        kind: 'CLASS',
        unitName: formData.unitName,
        groupCode: formData.groupCode,
        sections: sections, // Send array of strings
        assignedTeacherId: formData.assignedTeacherId || undefined,
        code: formData.groupCode ? `${formData.unitName}-${formData.groupCode}` : formData.unitName,
      };
      await onAdd(classData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm transition-all duration-300">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform scale-100 transition-all">
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
             <h3 className="text-base font-bold text-slate-800">Add New Class</h3>
             <p className="text-xs text-slate-500 mt-0.5">Create a new academic unit</p>
          </div>
          <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)' }}>
             <Plus size={18} />
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
              Class Grade / Level <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.unitName}
              onChange={(e) => setFormData(prev => ({ ...prev, unitName: e.target.value }))}
              className="block w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white shadow-sm text-sm py-2.5 px-4 transition-all input-field"
              placeholder="e.g. Grade 10"
              required
            />
          </div>
          
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
              Group Code <span className="text-gray-400 font-normal lowercase ml-1">(Optional)</span>
            </label>
            <input
              type="text"
              value={formData.groupCode}
              onChange={(e) => setFormData(prev => ({ ...prev, groupCode: e.target.value }))}
              className="block w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm py-2.5 px-4 transition-all"
              placeholder="e.g. PCM-001"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Assign Teacher <span className="text-gray-400 font-normal lowercase ml-1">(Optional)</span></label>
            <div className="relative">
              <select
                value={formData.assignedTeacherId}
                onChange={(e) => setFormData(prev => ({ ...prev, assignedTeacherId: e.target.value }))}
                className="block w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white shadow-sm text-sm py-2.5 px-4 appearance-none transition-all font-medium input-field"
              >
                <option value="">Choose a teacher...</option>
                {teachers?.map(teacher => (
                  <option key={teacher._id} value={teacher._id}>
                    {teacher.userName || teacher.name || 'Unnamed Teacher'}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Dynamic Sections Input */}
          <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
              Sections Management
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={currentSection}
                onChange={(e) => setCurrentSection(e.target.value)}
                onKeyDown={handleKeyDown}
              className="block flex-1 rounded-lg border-gray-200 bg-white shadow-sm text-sm py-2 px-3 transition-all placeholder:text-gray-400 input-field"
                placeholder="Type name (e.g. A) & Enter"
              />
              <button
                type="button"
                onClick={handleAddSection}
                disabled={!currentSection.trim()}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-all shadow-sm active:transform active:scale-95"
              >
                Add
              </button>
            </div>
            
            {/* Sections List */}
            {sections.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {sections.map((sec, idx) => (
                  <span key={idx} className="group inline-flex items-center pl-3 pr-2 py-1.5 rounded-lg bg-white border border-gray-200 text-sm font-medium text-slate-700 shadow-sm transition-all"
                    onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--primary-color, #4f46e5)'}
                    onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}>
                    <span className="mr-2">{sec}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSection(idx)}
                      className="text-gray-300 hover:text-red-500 bg-gray-50 hover:bg-red-50 rounded p-0.5 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            ) : (
                <div className="text-center py-3 border border-dashed border-gray-200 rounded-lg bg-white/50">
                    <p className="text-xs text-gray-400">No sections added yet</p>
                </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-slate-600 bg-white border border-gray-200 hover:bg-gray-50 font-medium transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isSubmitting ? 'Creating...' : 'Create Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Assign Teacher Modal Component (Unchanged)
const AssignTeacherModal = ({ classData, teachers, onClose, onAssign }) => {
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedTeacher && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await onAssign(classData._id, selectedTeacher);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm transition-all duration-300">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-lg font-bold text-slate-900">Assign Teacher</h3>
          <p className="text-sm text-slate-500 mt-1">
            For Class: <span className="font-semibold" style={{ color: 'var(--primary-color, #4f46e5)' }}>{classData.unitName}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {classData.assignedTeachers && (
            <div className="p-4 rounded-xl border flex items-center gap-4" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 5%, white)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }}>
               <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-lg border-2 border-white shadow-sm" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)', color: 'var(--primary-color, #4f46e5)' }}>
                  {(classData.assignedTeachers.userName || classData.assignedTeachers.name || 'T')[0]}
               </div>
               <div>
                  <p className="text-xs font-bold uppercase tracking-wide mb-0.5" style={{ color: 'color-mix(in srgb, var(--primary-color, #4f46e5) 60%, black)' }}>Current Teacher</p>
                  <p className="font-semibold text-slate-900">
                    {classData.assignedTeachers.userName || classData.assignedTeachers.name}
                  </p>
               </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Select Teacher</label>
            {teachers.length === 0 ? (
              <div className="text-sm text-amber-600 bg-amber-50 p-4 rounded-lg border border-amber-200 font-medium">
                No active teachers found. Please add teachers to the system first.
              </div>
            ) : (
              <div className="relative">
                <select
                  value={selectedTeacher}
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                  className="block w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm py-2.5 px-4 appearance-none transition-all font-medium"
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Choose a teacher...</option>
                  {teachers.map(teacher => (
                    <option key={teacher._id} value={teacher._id}>
                      {teacher.userName || teacher.name || 'Unnamed Teacher'}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-slate-600 bg-white border border-gray-200 hover:bg-gray-50 font-medium transition-colors text-sm"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || teachers.length === 0}
            >
              {isSubmitting ? 'Assigning...' : 'Confirm Assignment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Class Modal Component
const EditClassModal = ({ classData, onClose, onEdit }) => {
  const [formData, setFormData] = useState({
    unitName: classData.unitName || '',
    groupCode: classData.groupCode || '',
    code: classData.code || '',
  });
  
  const [existingSections, setExistingSections] = useState([]);
  const [newSections, setNewSections] = useState([]);
  const [currentSection, setCurrentSection] = useState("");
  const [loadingSections, setLoadingSections] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch sections on mount
  useEffect(() => {
    let active = true;
    const fetchSections = async () => {
        setLoadingSections(true);
        try {
            const response = await sectionApi.getSectionsByUnit(classData._id);
            if (active && response.success) {
                setExistingSections(response.data || []);
            }
        } catch (error) {
        } finally {
            if (active) setLoadingSections(false);
        }
    };
    fetchSections();
    return () => { active = false; };
  }, [classData._id]);

  const handleAddSection = () => {
    if (currentSection.trim()) {
        const val = currentSection.trim();
        // Check duplication in both existing and new
        const existsInExisting = existingSections.some(s => s.sectionName === val);
        const existsInNew = newSections.includes(val);
        
        if (!existsInExisting && !existsInNew) {
            setNewSections([...newSections, val]);
            setCurrentSection("");
        } else {
            toast.error("Section already exists");
        }
    }
  };

  const handleRemoveNewSection = (index) => {
    const arr = [...newSections];
    arr.splice(index, 1);
    setNewSections(arr);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSection();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        sections: newSections // Sending new sections relative to this edit session
      };

      await onEdit(classData._id, payload);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm transition-all duration-300">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform scale-100 transition-all">
        <div className="p-5 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-base font-bold text-slate-800">Edit Class</h3>
          <p className="text-xs text-slate-500 mt-0.5">Update details for <span className="font-semibold text-slate-700">{classData.unitName}</span></p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Unit Name</label>
            <input
              type="text"
              value={formData.unitName}
              onChange={(e) => setFormData(prev => ({ ...prev, unitName: e.target.value }))}
              className="block w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm py-2.5 px-4 transition-all"
              placeholder="e.g. Grade 10"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Group Code</label>
            <input
                type="text"
                value={formData.groupCode}
                onChange={(e) => setFormData(prev => ({ ...prev, groupCode: e.target.value }))}
                className="block w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white shadow-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm py-2.5 px-4 transition-all"
                placeholder="e.g. PCM"
            />
          </div>
          
          {/* Sections Management */}
          <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100">
             <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Sections Config</label>
             
             {/* Existing Sections Display */}
             {loadingSections ? (
                 <div className="text-xs text-slate-400 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--primary-color, #4f46e5)' }}></span>
                    Loading existing sections...
                 </div>
             ) : existingSections.length > 0 ? (
                 <div className="flex flex-wrap gap-2 mb-4">
                     {existingSections.map((sec) => (
                         <span key={sec._id} className="inline-flex items-center px-2.5 py-1 rounded border text-xs font-semibold" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)', color: 'var(--primary-color, #4f46e5)' }}>
                             {sec.sectionName}
                         </span>
                     ))}
                 </div>
             ) : (
                <div className="text-xs text-gray-400 italic mb-4">No existing sections found.</div>
             )}

             {/* Add New Sections */}
             <div className="flex gap-2 mb-3">
                <input
                    type="text"
                    value={currentSection}
                    onChange={(e) => setCurrentSection(e.target.value)}
                    onKeyDown={handleKeyDown}
                  className="block flex-1 rounded-lg border-gray-200 bg-white shadow-sm text-sm py-2 px-3 transition-all placeholder:text-gray-400 input-field"
                    placeholder="Add new section..."
                />
                <button
                    type="button"
                    onClick={handleAddSection}
                    disabled={!currentSection.trim()}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-all shadow-sm"
                >
                    Add
                </button>
             </div>
             
             {/* New Sections List */}
             {newSections.length > 0 && (
                 <div className="flex flex-col gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100/50">
                     <span className="text-[10px] text-amber-700 font-bold uppercase tracking-wider">Adding New Sections:</span>
                     <div className="flex flex-wrap gap-2">
                        {newSections.map((sec, idx) => (
                            <span key={idx} className="inline-flex items-center pl-2.5 pr-1.5 py-1 rounded bg-white border border-amber-200 text-xs font-medium text-slate-700 shadow-sm">
                                {sec}
                                <button
                                    type="button"
                                    onClick={() => handleRemoveNewSection(idx)}
                                    className="ml-1.5 text-amber-300 hover:text-red-500 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </span>
                        ))}
                     </div>
                 </div>
             )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Code <span className="text-gray-400 font-normal lowercase ml-1">(Optional)</span></label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
              className="block w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white shadow-sm text-sm py-2.5 px-4 transition-all input-field"
              placeholder="Enter class code"
            />
          </div>
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-slate-600 bg-white border border-gray-200 hover:bg-gray-50 font-medium transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ViewClassModal Component
const ViewClassModal = ({ classData, onClose }) => {
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchSections = async () => {
      if (!classData?._id) return;
      setLoadingSections(true);
      try {
        const response = await sectionApi.getSectionsByUnit(classData._id);
        if (active && response.success) {
          setSections(response.data || []);
        }
      } catch (error) {
      } finally {
        if (active) setLoadingSections(false);
      }
    };
    fetchSections();
    return () => { active = false; };
  }, [classData]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm transition-all duration-300">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform scale-100 transition-all">
        <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-start">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Class Details</h3>
            <p className="text-sm text-slate-500 mt-1">Full information for {classData.unitName}</p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-slate-600 transition-colors bg-white border border-gray-200 p-2 rounded-full hover:bg-gray-50 shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Unit Name</p>
              <p className="text-lg font-bold text-slate-900">{classData.unitName || 'N/A'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Group Code</p>
              <p className="text-sm font-semibold text-slate-700">{classData.groupCode || 'N/A'}</p>
            </div>
          </div>

           {/* Sections Display */}
           <div className="space-y-2">
             <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Sections</p>
             {loadingSections ? (
               <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--primary-color, #4f46e5)' }}></span>
                  Loading sections...
               </div>
             ) : sections.length > 0 ? (
               <div className="flex flex-wrap gap-2">
                 {sections.map(section => (
                   <span key={section._id} className="inline-flex items-center px-2.5 py-1 rounded-lg border text-xs font-semibold" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)', color: 'var(--primary-color, #4f46e5)' }}>
                     {section.name || section.sectionName}
                   </span>
                 ))}
               </div>
             ) : (
                <p className="text-sm text-gray-500 italic">No sections configured</p>
             )}
           </div>
           
           <div className="space-y-1">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Code</p>
              <p className="text-sm font-semibold text-slate-700 font-mono bg-gray-50 inline-block px-3 py-1 rounded-lg border border-gray-200">
                {classData.code || 'N/A'}
              </p>
            </div>

          <div className="space-y-3 pt-4 border-t border-gray-100">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Assigned Teacher</p>
            {classData.assignedTeachers ? (
              <div className="flex items-center gap-4 p-4 rounded-xl border" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 5%, white)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }}>
                <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center font-bold border-2 shadow-sm text-lg" style={{ color: 'var(--primary-color, #4f46e5)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)' }}>
                  {(classData.assignedTeachers.userName || classData.assignedTeachers.name || 'T')[0]}
                </div>
                <div>
                  <p className="text-base font-bold text-slate-900">
                    {classData.assignedTeachers.userName || classData.assignedTeachers.name}
                  </p>
                  <p className="text-xs font-medium px-2 py-0.5 rounded-full inline-block mt-0.5" style={{ color: 'var(--primary-color, #4f46e5)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)' }}>
                    {classData.assignedTeachers.userRole}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center">
                 <p className="text-sm italic text-gray-500 font-medium">No teacher currently assigned</p>
              </div>
            )}
          </div>

          {/* Internal ID removed as per request */}
        </div>
        
        <div className="p-4 bg-gray-50/50 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-slate-700 bg-white border border-gray-200 hover:bg-gray-50 font-semibold shadow-sm transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Classes;
